import {
    AssertionDescriptorUsageTypesEnum,
    AssertionDescriptorValueDisplayTypes,
    ConditionSelectorEnums,
} from '../../../../utilities/Constants';

import { ConditionSelector } from '../../../../pages/query/ConditionSelector';
import { IAssertionDescriptorModel } from '../../../../interfaces/IAssertionDescriptor';
import { IQueryFilterCriteria } from '../../../../interfaces/IQuery';
import { IUserModel } from '../../../../interfaces/IUser';
import { IValidation } from '../../../../interfaces/IError';
import { JSX } from 'react';
import { TriggerFields } from './TriggerFields';
import { cloneDeep } from 'lodash';
import { useZoneGetAssertionDescriptors } from '../../../../shared/react-query-hooks/useZoneGetAssertionDescriptors';

export interface ITriggerFieldsSectionProps {
    availableFilterCriterias: IQueryFilterCriteria[];
    buildValueControls: (item: IQueryFilterCriteria) => JSX.Element;
    onConditionOptionChange: (item: IQueryFilterCriteria, itemIndex: number) => void;
    onSelectedTriggerFieldsChange: (selectedCriteria: IQueryFilterCriteria[]) => void;
    queryType: number;
    selectedTriggerFields: IQueryFilterCriteria[];
    suppressDateRangeCondition?: boolean;
    user: IUserModel;
    validation: IValidation;
    isQuadScope?: boolean;
    assertionDescriptors: IAssertionDescriptorModel[];
}

export function TriggerFieldsSection(props: ITriggerFieldsSectionProps) {
    const handleTriggerFieldsChange = (field: IQueryFilterCriteria[]) => {
        props.onSelectedTriggerFieldsChange(field);
    };

    const handleConditionOptionChange = (fields: IQueryFilterCriteria[], fieldIdx: number) => {
        props.onConditionOptionChange(fields[fieldIdx], fieldIdx);
    };

    const handleUpdateTriggerField = (toUpdate: IQueryFilterCriteria) => {
        const selectedTriggerFieldsCopy = cloneDeep(props.selectedTriggerFields);

        const existingIndex = selectedTriggerFieldsCopy.findIndex((field) => {
            if (field.isAssertionDescriptor) {
                return field.assertionDescriptorGuid === toUpdate.assertionDescriptorGuid;
            }
            return field.staticItemId === toUpdate.staticItemId;
        });

        selectedTriggerFieldsCopy.splice(existingIndex, 1, toUpdate);
        props.onSelectedTriggerFieldsChange(selectedTriggerFieldsCopy);
    };

    const handleDeleteTriggerField = (toDelete: IQueryFilterCriteria) => {
        const selectedTriggerFieldsCopy = cloneDeep(props.selectedTriggerFields);

        const existingIndex = selectedTriggerFieldsCopy.findIndex((field) => {
            if (field.isAssertionDescriptor) {
                return field.assertionDescriptorGuid === toDelete.assertionDescriptorGuid;
            }
            return field.staticItemId === toDelete.staticItemId;
        });

        selectedTriggerFieldsCopy.splice(existingIndex, 1);
        props.onSelectedTriggerFieldsChange(selectedTriggerFieldsCopy);
    };

    return (
        <>
            <TriggerFields
                onTriggerFieldSelected={handleTriggerFieldsChange}
                availableTriggerFields={props.availableFilterCriterias}
                selectedTriggerFields={props.selectedTriggerFields ?? []}
                validation={props.validation}
            />
            <TriggerFieldsValue
                onConditionOptionChange={handleConditionOptionChange}
                onDeleteField={handleDeleteTriggerField}
                onUpdateField={handleUpdateTriggerField}
                selectedTriggerFields={props.selectedTriggerFields}
                suppressDateRangeCondition={props.suppressDateRangeCondition}
                valuesColumn={props.buildValueControls}
                isQuadScope={props.isQuadScope}
                assertionDescriptors={props.assertionDescriptors}
            />
        </>
    );
}

interface ITriggerFieldsValueProps {
    valuesColumn: (item: IQueryFilterCriteria) => JSX.Element | JSX.Element[];
    onConditionOptionChange: (filters: IQueryFilterCriteria[], filterIdx: number) => void;
    onDeleteField: (item: IQueryFilterCriteria) => void;
    onUpdateField: (item: IQueryFilterCriteria) => void;
    selectedTriggerFields: IQueryFilterCriteria[];
    suppressDateRangeCondition?: boolean;
    isQuadScope?: boolean;
    assertionDescriptors: IAssertionDescriptorModel[];
}
function TriggerFieldsValue(props: ITriggerFieldsValueProps) {
    return (
        <>
            {props.selectedTriggerFields.length > 0 && (
                <div className="row my-2">
                    <div className="col-sm-2" />
                    <div className="col-sm-10">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th className="col-sm-3">Trigger Field</th>
                                    <th className="col-sm-2">Condition</th>
                                    <th className="col-sm-7">Values</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {props.selectedTriggerFields.map((field, i) => (
                                    <TriggerFieldRow
                                        assertionDescriptors={props.assertionDescriptors ?? []}
                                        key={i}
                                        field={field}
                                        onConditionOptionChange={props.onConditionOptionChange}
                                        onDeleteClick={props.onDeleteField}
                                        onUpdateField={props.onUpdateField}
                                        selectedFields={props.selectedTriggerFields}
                                        suppressDateRangeCondition={
                                            props.suppressDateRangeCondition
                                        }
                                        valuesColumn={props.valuesColumn(field)}
                                        isQuadScope={props.isQuadScope}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}

export interface ITriggerFieldRowProps {
    assertionDescriptors: IAssertionDescriptorModel[];
    field: IQueryFilterCriteria;
    onConditionOptionChange: (filters: IQueryFilterCriteria[], filterIdx: number) => void;
    onDeleteClick: (field: IQueryFilterCriteria) => void;
    onUpdateField: (field: IQueryFilterCriteria) => void;
    selectedFields: IQueryFilterCriteria[];
    suppressDateRangeCondition?: boolean;
    valuesColumn: JSX.Element | JSX.Element[];
    isQuadScope?: boolean;
}

const conditionOpts = [
    {
        label: ConditionSelectorEnums.AllChanges,
        value: ConditionSelectorEnums.AllChanges,
    },
    {
        label: ConditionSelectorEnums.EqualTo,
        value: ConditionSelectorEnums.EqualTo,
    },
    {
        label: ConditionSelectorEnums.Range,
        value: ConditionSelectorEnums.Range,
    },
];

export function TriggerFieldRow(props: ITriggerFieldRowProps) {
    const {
        assertionDescriptors,
        field,
        onConditionOptionChange,
        onDeleteClick,
        selectedFields,
        valuesColumn,
        isQuadScope,
    } = props;

    const assertionDescriptor = getSelectedAssertionDescriptor();

    function getSelectedAssertionDescriptor() {
        const assertionDescriptorMatch = assertionDescriptors.find((x) => {
            return (
                !!field.assertionDescriptorGuid &&
                !!x.guid &&
                x.guid.toUpperCase() === field.assertionDescriptorGuid.toUpperCase()
            );
        });
        return assertionDescriptorMatch;
    }

    const handleConditionOptionChange = (selectedItem: any) => {
        const triggerFieldSelected = [...selectedFields];

        const filterIdx = triggerFieldSelected.findIndex((f) => f.value === field.value) ?? -1;
        const filtersCopy = [...triggerFieldSelected];
        const filterItemToModify = filtersCopy[filterIdx];

        const conditionValue = selectedItem?.value;

        filterItemToModify.condition = conditionValue;
        filterItemToModify.selectedValues = [];
        filterItemToModify.text = '';
        filterItemToModify.startDate = undefined;
        filterItemToModify.endDate = undefined;
        filterItemToModify.booleanValue = undefined;
        filterItemToModify.numericValueFrom = undefined;
        filterItemToModify.numericValueFromText = '';
        filterItemToModify.numericValueTo = undefined;
        filterItemToModify.numericValueToText = '';
        filterItemToModify.validationError = '';
        filterItemToModify.dateRangeInterval = undefined;
        filterItemToModify.triggerAllChanges = conditionValue === ConditionSelectorEnums.AllChanges;

        onConditionOptionChange(filtersCopy, filterIdx);
    };

    let optionsToRender = [...conditionOpts];
    if (isQuadScope) {
        optionsToRender = optionsToRender.filter(
            (opt) => opt.value === ConditionSelectorEnums.AllChanges
        );
    } else {
        const isDateFieldSelected = assertionDescriptor?.valueDisplayType?.id === AssertionDescriptorValueDisplayTypes.DateTime.Value;

        if (!isDateFieldSelected || props.suppressDateRangeCondition) {
            optionsToRender = optionsToRender.filter(
                (opt) => opt.value !== ConditionSelectorEnums.Range
            );
        }
    }

    const handleDeleteClick = () => onDeleteClick(field);

    return (
        <tr>
            <td>{field.label}</td>
            <td style={{ paddingRight: 15 }}>
                <ConditionSelector
                    options={optionsToRender}
                    onChange={handleConditionOptionChange}
                    value={field.condition}
                />
            </td>
            <td style={{ paddingRight: '2em' }}>{valuesColumn}</td>
            <td width="5%" className="pt-1">
                {!field.readOnly && (
                    <button className="btn btn-gray btn-icon float-end" onClick={handleDeleteClick}>
                        <i className="fal fa-times color-white" />
                    </button>
                )}
            </td>
        </tr>
    );
}
